// src/utils/videoProcessing.js
// Real browser-side video analysis using HTMLVideoElement + Canvas API
// Extracts frames → pixel analysis → motion detection → scene detection → color palettes

// ─────────────────────────────────────────────
// FRAME EXTRACTION
// ─────────────────────────────────────────────

// Extract a single frame from a video at a given time
export const extractFrame = (videoEl, time, maxDim = 320) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    let w = videoEl.videoWidth, h = videoEl.videoHeight;
    if (w > maxDim || h > maxDim) {
      const scale = Math.min(maxDim / w, maxDim / h);
      w = Math.floor(w * scale); h = Math.floor(h * scale);
    }
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    const seek = () => {
      ctx.drawImage(videoEl, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      resolve({ imageData, dataUrl, time, w, h });
    };

    if (Math.abs(videoEl.currentTime - time) < 0.05) {
      seek();
    } else {
      videoEl.currentTime = time;
      videoEl.onseeked = seek;
    }
  });
};

// Extract N frames evenly spaced across the video
export const extractFrames = async (videoEl, numFrames = 12, onProgress) => {
  const duration = videoEl.duration;
  const frames = [];
  for (let i = 0; i < numFrames; i++) {
    const time = (i / numFrames) * duration;
    const frame = await extractFrame(videoEl, time);
    frames.push(frame);
    if (onProgress) onProgress(Math.round((i + 1) / numFrames * 100));
  }
  return frames;
};

// ─────────────────────────────────────────────
// FRAME PIXEL ANALYSIS
// ─────────────────────────────────────────────

// Get mean color (R, G, B) of a frame
export const getFrameMeanColor = (imageData) => {
  const d = imageData.data;
  let r = 0, g = 0, b = 0;
  const pixels = d.length / 4;
  for (let i = 0; i < d.length; i += 4) {
    r += d[i]; g += d[i + 1]; b += d[i + 2];
  }
  return { r: Math.round(r / pixels), g: Math.round(g / pixels), b: Math.round(b / pixels) };
};

// Get brightness of a frame (0-255)
export const getFrameBrightness = (imageData) => {
  const d = imageData.data;
  let luma = 0;
  const pixels = d.length / 4;
  for (let i = 0; i < d.length; i += 4) {
    luma += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  }
  return Math.round(luma / pixels);
};

// Get contrast of a frame (std dev of luma)
export const getFrameContrast = (imageData) => {
  const d = imageData.data;
  const pixels = d.length / 4;
  const lumas = [];
  for (let i = 0; i < d.length; i += 4) {
    lumas.push(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
  }
  const mean = lumas.reduce((s, v) => s + v, 0) / pixels;
  const variance = lumas.reduce((s, v) => s + (v - mean) ** 2, 0) / pixels;
  return Math.round(Math.sqrt(variance));
};

// Compute diff between two frames (motion amount)
export const getFrameDiff = (imgData1, imgData2) => {
  if (!imgData1 || !imgData2) return 0;
  const d1 = imgData1.data, d2 = imgData2.data;
  const len = Math.min(d1.length, d2.length);
  let totalDiff = 0;
  for (let i = 0; i < len; i += 4) {
    const dr = Math.abs(d1[i] - d2[i]);
    const dg = Math.abs(d1[i + 1] - d2[i + 1]);
    const db = Math.abs(d1[i + 2] - d2[i + 2]);
    totalDiff += (dr + dg + db) / 3;
  }
  return +(totalDiff / (len / 4)).toFixed(2);
};

// ─────────────────────────────────────────────
// COLOR PALETTE EXTRACTION (k-means lite)
// ─────────────────────────────────────────────

export const extractColorPalette = (imageData, k = 8) => {
  const d = imageData.data;
  const pixels = [];

  // Subsample pixels
  for (let i = 0; i < d.length; i += 16) {  // every 4 pixels
    if (d[i + 3] > 127) pixels.push([d[i], d[i + 1], d[i + 2]]);
  }

  if (pixels.length < k) return [];

  // Initialize centroids randomly from pixels
  let centroids = Array.from({ length: k }, () => {
    const p = pixels[Math.floor(Math.random() * pixels.length)];
    return [...p];
  });

  // Run 10 iterations of k-means
  for (let iter = 0; iter < 10; iter++) {
    const clusters = Array.from({ length: k }, () => ({ sum: [0, 0, 0], count: 0 }));
    pixels.forEach(p => {
      let minDist = Infinity, minIdx = 0;
      centroids.forEach((c, ci) => {
        const dist = (p[0]-c[0])**2 + (p[1]-c[1])**2 + (p[2]-c[2])**2;
        if (dist < minDist) { minDist = dist; minIdx = ci; }
      });
      clusters[minIdx].sum[0] += p[0];
      clusters[minIdx].sum[1] += p[1];
      clusters[minIdx].sum[2] += p[2];
      clusters[minIdx].count++;
    });
    centroids = clusters.map((cl, i) => cl.count > 0
      ? [Math.round(cl.sum[0]/cl.count), Math.round(cl.sum[1]/cl.count), Math.round(cl.sum[2]/cl.count)]
      : centroids[i]
    );
  }

  // Count cluster sizes
  const counts = new Array(k).fill(0);
  pixels.forEach(p => {
    let minDist = Infinity, minIdx = 0;
    centroids.forEach((c, ci) => {
      const dist = (p[0]-c[0])**2 + (p[1]-c[1])**2 + (p[2]-c[2])**2;
      if (dist < minDist) { minDist = dist; minIdx = ci; }
    });
    counts[minIdx]++;
  });

  return centroids
    .map((c, i) => ({
      r: c[0], g: c[1], b: c[2],
      hex: `#${c[0].toString(16).padStart(2,'0')}${c[1].toString(16).padStart(2,'0')}${c[2].toString(16).padStart(2,'0')}`,
      count: counts[i],
      pct: +(counts[i] / pixels.length * 100).toFixed(1),
    }))
    .sort((a, b) => b.count - a.count);
};

// ─────────────────────────────────────────────
// SCENE DETECTION
// ─────────────────────────────────────────────

export const detectScenes = (frames, threshold = 20) => {
  const scenes = [];
  let sceneStart = 0;

  for (let i = 1; i < frames.length; i++) {
    const diff = getFrameDiff(frames[i - 1].imageData, frames[i].imageData);
    if (diff > threshold || i === frames.length - 1) {
      scenes.push({
        startTime: frames[sceneStart].time,
        endTime: frames[i - 1].time,
        startFrame: sceneStart,
        endFrame: i - 1,
        duration: +(frames[i - 1].time - frames[sceneStart].time).toFixed(2),
        thumbnail: frames[sceneStart].dataUrl,
        diff: +diff.toFixed(1),
      });
      sceneStart = i;
    }
  }

  return scenes;
};

// ─────────────────────────────────────────────
// PER-FRAME TIMELINE DATA
// ─────────────────────────────────────────────

export const buildTimeline = (frames) => {
  return frames.map((frame, i) => {
    const brightness = getFrameBrightness(frame.imageData);
    const contrast = getFrameContrast(frame.imageData);
    const color = getFrameMeanColor(frame.imageData);
    const motion = i > 0 ? getFrameDiff(frames[i - 1].imageData, frame.imageData) : 0;
    return {
      time: +frame.time.toFixed(2),
      brightness,
      contrast,
      motion: +motion.toFixed(1),
      r: color.r, g: color.g, b: color.b,
      luma: Math.round(0.299 * color.r + 0.587 * color.g + 0.114 * color.b),
    };
  });
};

// ─────────────────────────────────────────────
// VIDEO METADATA
// ─────────────────────────────────────────────

export const getVideoMetadata = (videoEl, file) => {
  return {
    width: videoEl.videoWidth,
    height: videoEl.videoHeight,
    duration: +videoEl.duration.toFixed(2),
    aspectRatio: (videoEl.videoWidth / videoEl.videoHeight).toFixed(2),
    fps: 'N/A (use MediaInfo for exact FPS)',
    resolution: `${videoEl.videoWidth}×${videoEl.videoHeight}`,
    megapixels: +((videoEl.videoWidth * videoEl.videoHeight) / 1_000_000).toFixed(2),
    fileName: file?.name || 'unknown',
    fileSize: file ? (file.size / (1024 * 1024)).toFixed(1) + ' MB' : 'N/A',
    fileType: file?.type || 'video',
  };
};

// ─────────────────────────────────────────────
// HISTOGRAM OVER TIME (channel evolution)
// ─────────────────────────────────────────────

export const getChannelEvolution = (frames) => {
  // Returns per-frame R/G/B mean — for line chart over time
  return frames.map(frame => {
    const color = getFrameMeanColor(frame.imageData);
    const brightness = getFrameBrightness(frame.imageData);
    return {
      time: +frame.time.toFixed(2),
      R: color.r, G: color.g, B: color.b,
      brightness,
    };
  });
};

// ─────────────────────────────────────────────
// OPTICAL FLOW (simplified block-based motion vectors)
// ─────────────────────────────────────────────

export const computeMotionVectors = (frame1, frame2, blockSize = 16) => {
  if (!frame1 || !frame2) return [];
  const w = frame1.width || Math.sqrt(frame1.imageData?.data.length / 4);
  const h = frame1.height || w;
  const d1 = frame1.imageData.data;
  const d2 = frame2.imageData.data;
  const vectors = [];

  // Compare blocks between frames
  for (let by = 0; by < h - blockSize; by += blockSize * 2) {
    for (let bx = 0; bx < w - blockSize; bx += blockSize * 2) {
      let sumDiff = 0, count = 0;
      // Compare luminance in block
      for (let y = 0; y < blockSize; y++) {
        for (let x = 0; x < blockSize; x++) {
          const idx = ((by + y) * w + (bx + x)) * 4;
          if (idx + 2 < d1.length) {
            const luma1 = 0.299 * d1[idx] + 0.587 * d1[idx+1] + 0.114 * d1[idx+2];
            const luma2 = 0.299 * d2[idx] + 0.587 * d2[idx+1] + 0.114 * d2[idx+2];
            sumDiff += Math.abs(luma1 - luma2);
            count++;
          }
        }
      }
      const mag = count > 0 ? sumDiff / count : 0;
      if (mag > 5) { // only non-static blocks
        vectors.push({ x: bx + blockSize/2, y: by + blockSize/2, magnitude: +mag.toFixed(1) });
      }
    }
  }
  return vectors;
};

// ─────────────────────────────────────────────
// COLOR SPACE CONVERSION
// ─────────────────────────────────────────────

export const rgbToHsv = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h, s = max === 0 ? 0 : d / max, v = max;
  if (max === min) h = 0;
  else if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: +(h * 360).toFixed(1), s: +(s * 100).toFixed(1), v: +(v * 100).toFixed(1) };
};

// Analyze hue distribution across a frame
export const getHueDistribution = (imageData, bins = 36) => {
  const d = imageData.data;
  const hueBins = new Array(bins).fill(0);
  for (let i = 0; i < d.length; i += 8) {
    const { h } = rgbToHsv(d[i], d[i+1], d[i+2]);
    const bin = Math.min(bins - 1, Math.floor(h / 360 * bins));
    hueBins[bin]++;
  }
  return hueBins.map((count, i) => ({
    hue: Math.round(i * 360 / bins),
    count,
    color: `hsl(${Math.round(i * 360 / bins)}, 80%, 55%)`,
  }));
};

// ─────────────────────────────────────────────
// AUDIO EXTRACTION FROM VIDEO (via Web Audio API)
// ─────────────────────────────────────────────

export const analyzeVideoAudio = async (file) => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.getChannelData(0);
    const duration = audioBuffer.duration;
    const sampleRate = audioBuffer.sampleRate;

    // RMS energy over time (50 windows)
    const numWindows = 50;
    const windowSize = Math.floor(channelData.length / numWindows);
    const energyOverTime = [];
    for (let i = 0; i < numWindows; i++) {
      let sum = 0;
      for (let j = 0; j < windowSize; j++) {
        sum += channelData[i * windowSize + j] ** 2;
      }
      energyOverTime.push({
        time: +(i * duration / numWindows).toFixed(2),
        rms: +Math.sqrt(sum / windowSize).toFixed(4),
      });
    }

    const peak = Math.max(...Array.from(channelData).map(Math.abs));
    const rms = Math.sqrt(Array.from(channelData).reduce((s, v) => s + v*v, 0) / channelData.length);

    return { energyOverTime, peak: +peak.toFixed(4), rms: +rms.toFixed(4), duration: +duration.toFixed(2), sampleRate, hasAudio: true };
  } catch (e) {
    return { hasAudio: false, error: e.message };
  }
};